<script lang="ts">
  export let title = '';
  export let subtitle = '';
</script>

<main class="auth-page">
  <div class="auth-card">
    <header class="auth-header">
      <div class="auth-header-brand">
        <div class="auth-header-logo-wrap">
          <img src="/logo.png" alt="" class="auth-header-logo" />
        </div>
        <div class="auth-header-text">
          <h1>{title}</h1>
          {#if subtitle}
            <p class="auth-subtitle">{subtitle}</p>
          {/if}
        </div>
      </div>
    </header>

    <div class="auth-body">
      <slot />
    </div>

    {#if $$slots.footer}
      <footer class="auth-footer">
        <slot name="footer" />
      </footer>
    {/if}
  </div>
</main>

<style>
  .auth-page {
    --auth-surface: #fffdf9;
    --auth-surface-muted: #faf8f4;
    --auth-ink: #1f2937;
    --auth-ink-muted: #6b7280;
    --auth-border: rgba(0, 0, 0, 0.08);
    --auth-shadow: 0 4px 24px rgba(52, 58, 64, 0.08);

    width: 100%;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem 1rem;
    box-sizing: border-box;
    background-color: #f5f3ef;
    background-image: url('/background.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }

  @keyframes auth-card-enter {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .auth-card {
    max-width: 440px;
    width: 100%;
    margin: 0 auto;
    padding: 1.375rem 1.625rem 1.5rem;
    background: var(--auth-surface);
    border: 1px solid var(--auth-border);
    border-radius: 16px;
    box-shadow: var(--auth-shadow);
    animation: auth-card-enter 0.42s ease-out both;
  }

  .auth-header {
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .auth-header-brand {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .auth-header-logo-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border-radius: 14px;
    background: var(--auth-surface-muted);
    border: 1px solid var(--auth-border);
  }

  .auth-header-logo {
    height: 60px;
    width: auto;
    display: block;
  }

  .auth-header-text h1 {
    margin: 0 0 0.25rem;
    text-align: left;
    font-size: 1.625rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--auth-ink);
  }

  .auth-subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: var(--auth-ink-muted);
    line-height: 1.4;
    font-weight: 500;
  }

  .auth-footer {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    text-align: center;
    color: var(--auth-ink-muted);
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .auth-footer :global(a) {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 600;
    margin-left: 0.25rem;
  }

  .auth-footer :global(a:hover) {
    text-decoration: underline;
  }

  .auth-body :global(.auth-loading) {
    text-align: center;
    padding: 1.5rem 0;
    color: var(--auth-ink-muted);
  }

  .auth-body :global(.auth-spinner) {
    width: 3rem;
    height: 3rem;
    border: 4px solid #f3f4f6;
    border-top: 4px solid var(--color-primary);
    border-radius: 50%;
    animation: auth-spin 1s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes auth-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .auth-body :global(.auth-status-panel) {
    text-align: center;
  }

  .auth-body :global(.auth-status-panel p) {
    margin: 0 0 0.75rem;
    color: var(--auth-ink-muted);
    font-size: 0.9375rem;
    line-height: 1.6;
    text-align: left;
  }

  .auth-body :global(.auth-status-icon) {
    margin: 0 auto 1rem;
    display: block;
  }

  .auth-body :global(.auth-status-icon.success) {
    color: var(--color-positive);
  }

  .auth-body :global(.auth-status-icon.error) {
    color: var(--color-negative);
  }

  .auth-body :global(.auth-actions) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.25rem;
  }

  .auth-body :global(.auth-btn) {
    width: 100%;
    padding: 0.9rem;
    background-color: var(--color-primary);
    color: var(--color-text-dark);
    border: none;
    border-radius: 25rem;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    box-shadow: 0 2px 10px rgba(255, 180, 0, 0.3);
  }

  .auth-body :global(.auth-btn:hover:not(:disabled)) {
    background-color: var(--color-primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(255, 180, 0, 0.35);
  }

  .auth-body :global(.auth-btn:active:not(:disabled)) {
    transform: translateY(0);
  }

  .auth-body :global(.auth-btn:disabled) {
    background-color: var(--color-disabled-bg);
    color: var(--color-disabled-text);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  .auth-body :global(.auth-btn-secondary) {
    width: 100%;
    padding: 0.9rem;
    background-color: #e5e7eb;
    color: #374151;
    border: 1px solid #d1d5db;
    border-radius: 25rem;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    transition: background-color 0.2s ease;
  }

  .auth-body :global(.auth-btn-secondary:hover:not(:disabled)) {
    background-color: #d1d5db;
  }

  .auth-body :global(.auth-form-error) {
    background-color: var(--color-negative-bg);
    color: var(--color-negative);
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .auth-body :global(.auth-form-section) {
    margin-bottom: 1.25rem;
  }

  .auth-body :global(.auth-form-section-title) {
    margin: 0 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-secondary);
    text-align: left;
  }

  .auth-body :global(.auth-form-section-body) {
    padding: 1rem 1.125rem;
    background: var(--auth-surface-muted);
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 12px;
  }

  .auth-body :global(.auth-form-group) {
    margin-bottom: 0.875rem;
  }

  .auth-body :global(.auth-form-section-body .auth-form-group:last-of-type) {
    margin-bottom: 0;
  }

  .auth-body :global(.auth-form-group label) {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    text-align: left;
  }

  .auth-body :global(.auth-required) {
    color: var(--color-negative);
  }

  .auth-body :global(.auth-input) {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;
    font-family: inherit;
    box-sizing: border-box;
    background: white;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .auth-body :global(.auth-input.error) {
    border-color: #ef4444;
  }

  .auth-body :global(.auth-input:focus) {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.15);
  }

  .auth-body :global(.auth-field-error) {
    margin: 0.375rem 0 0;
    font-size: 0.75rem;
    color: #ef4444;
    text-align: left;
  }

  @media (max-width: 768px) {
    .auth-page {
      align-items: flex-start;
      padding: 1.25rem 0.5rem 1.5rem;
    }

    .auth-card {
      padding: 1rem 1.125rem 1.25rem;
    }

    .auth-header-logo-wrap {
      padding: 0.375rem;
      border-radius: 12px;
    }

    .auth-header-logo {
      height: 48px;
    }

    .auth-header-text h1 {
      font-size: 1.3125rem;
    }

    .auth-subtitle {
      font-size: 0.8125rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .auth-card {
      animation: none;
    }

    .auth-body :global(.auth-btn) {
      transition: none;
    }

    .auth-body :global(.auth-btn:hover:not(:disabled)) {
      transform: none;
    }

    .auth-body :global(.auth-spinner) {
      animation: none;
    }
  }
</style>
